import API from "../http";

const DEMO_KG_ACCESS = ["VIEW", "EDIT", "INSERT", "DELETE"];

function normKey(k) {
  return typeof k === "string" ? k.replace(/^\//, "") : "";
}

/** Батьківський модуль садочка: у children є маршрути kindergarten/{id}/… */
export function isKindergartenParentModule(section) {
  if (!section?.children?.length) return false;
  return section.children.some((c) => /^kindergarten\/\d+\//.test(normKey(c?.key)));
}

export function extractKindergartenIdFromKey(key) {
  const m = normKey(key).match(/^kindergarten\/(\d+)\//);
  return m ? m[1] : null;
}

function getKindergartenIdFromSection(section) {
  for (const c of section?.children || []) {
    const id = extractKindergartenIdFromKey(c?.key);
    if (id) return id;
  }
  return null;
}

export function collectKindergartenIdsFromMenu(accessGroup) {
  const ids = new Set();
  for (const section of accessGroup || []) {
    for (const c of section?.children || []) {
      const kid = extractKindergartenIdFromKey(c?.key);
      if (kid) ids.add(kid);
    }
  }
  return ids;
}

function remapKindergartenPath(value, fromKgId, toKgId) {
  const s = String(value || "");
  if (!s) return s;
  return s
    .replace(new RegExp(`kindergarten/${fromKgId}/`, "g"), `kindergarten/${toKgId}/`)
    .replace(new RegExp(`^kindergarten/${fromKgId}$`), `kindergarten/${toKgId}`);
}

export function cloneKindergartenMenuSection(template, fromKgId, toKgId, displayName) {
  const children = (template.children || []).map((child) => {
    const key = normKey(child.key);
    const moduleId = String(child.module_id || key);
    return {
      ...child,
      key: remapKindergartenPath(key, fromKgId, toKgId),
      module_id: remapKindergartenPath(moduleId, fromKgId, toKgId),
      access: Array.isArray(child.access) && child.access.length
        ? [...child.access]
        : [...DEMO_KG_ACCESS],
    };
  });

  return {
    ...template,
    module_id: `demo-kindergarten-parent-${toKgId}`,
    module_name: displayName,
    children,
  };
}

/** Додає «Про нас» для кожного kindergarten/{id}/… у children. */
export function injectDemoKindergartenAboutMenuItems(section) {
  if (!section || !Array.isArray(section.children) || section.children.length === 0) {
    return section;
  }
  const ids = new Set();
  for (const c of section.children) {
    const match = normKey(c?.key).match(/^kindergarten\/(\d+)\//);
    if (match) ids.add(match[1]);
  }
  if (ids.size === 0) return section;
  const existing = new Set(section.children.map((c) => normKey(c.key)));
  const extras = [];
  for (const kid of ids) {
    const key = `kindergarten/${kid}/about`;
    if (existing.has(key)) continue;
    extras.push({
      key,
      module_name: "Про нас",
      module_id: `kindergarten/${kid}/about`,
      access: [...DEMO_KG_ACCESS],
    });
    existing.add(key);
  }
  if (!extras.length) return section;
  return { ...section, children: [...section.children, ...extras] };
}

export async function fetchDemoKindergartensList() {
  try {
    const res = await API.get("api/kindergarten/kindergartens");
    const data = res?.data;
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

/**
 * Для демо: додати в меню садочки з довідника, яких немає в access_group з auth.
 * Шаблон підменю клонується з першого наявного модуля садочка.
 */
export function expandDemoKindergartenMenu(accessGroup, kindergartens = []) {
  if (!Array.isArray(accessGroup) || !kindergartens.length) return accessGroup;

  const template = accessGroup.find(isKindergartenParentModule);
  if (!template) return accessGroup;

  const existingIds = collectKindergartenIdsFromMenu(accessGroup);
  const fromKgId =
    getKindergartenIdFromSection(template) ||
    [...existingIds][0] ||
    extractKindergartenIdFromKey(template.children?.[0]?.key);

  if (!fromKgId) return accessGroup;

  const sorted = [...kindergartens].sort((a, b) => Number(a.id) - Number(b.id));
  const extraSections = [];

  for (const kg of sorted) {
    const id = String(kg.id);
    if (existingIds.has(id)) continue;
    extraSections.push(
      cloneKindergartenMenuSection(
        template,
        fromKgId,
        id,
        kg.name || `Садочок ${id}`,
      ),
    );
  }

  if (!extraSections.length) return accessGroup;

  const result = [...accessGroup];
  let lastKgIdx = -1;
  for (let i = 0; i < result.length; i += 1) {
    if (isKindergartenParentModule(result[i])) lastKgIdx = i;
  }
  result.splice(lastKgIdx + 1, 0, ...extraSections);
  return result;
}
